import angular from "angular";
import { ApiCheckStatus, ApiValidationErrorType, ApiImportStatus, Dates, Arrays, UserDefinedRow, Imports, DataMapper, DataMapperSelector, CalculationImportRow, CalculationImport, WorkerCheck, Translations, PayrollCalculationsSaveOptions, RowMergeOption } from "@salaxy/core";

import { DataImportControllerBase } from "./DataImportControllerBase";

import { JsonSchemaService } from "../../../services";

/**
 * Controller for calculation imports.
 */
export class CalcImportController extends DataImportControllerBase<CalculationImportRow, CalculationImport, PayrollCalculationsSaveOptions> {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["Imports", "JsonSchemaService", "$timeout"];

  /** Boolean indicating if the worker validation is in progress. */

  public isValidating = false;

  /** Options form */
  public optionsForm: angular.IFormController;

  /** Custom options */
  public customOptions: "addRows" | "removeImportedRows" | "removeAllRows" = "removeAllRows";

  // check SalaryDate, should not be changed in server
  private getTargetKey = (x: CalculationImport) => `${x.officialId}_${x.item.worker.paymentData.socialSecurityNumber}_${Dates.asDate(x.item.workflow.salaryDate)}_${x.parameters.payroll.input.title}`.toLowerCase();
  private getSourceKey = (x: CalculationImportRow) => `${x.companyOfficialId}_${x.personalId}_${Dates.asDate(x.salaryDate)}_${x.payrollTitle}`.toLowerCase();
  private getCheckTargetKey = (x: WorkerCheck) => `${x.officialId}_${x.id}`.toLowerCase();
  private getCheckSourceKey = (x: CalculationImportRow) => `${x.companyOfficialId}_${x.employmentId || x.personalId}`.toLowerCase();

  private extraValidator = {
    validate: () => this.validateWorkers(),
    buttonLabel: Translations.get("SALAXY.NG1.CalcImport.validateWorkersButton")
  };

  /**
   * Creates a new WorkerImportController
   * @ignore
   */
  constructor(
    private importsApi: Imports,
    jsonSchemaService: JsonSchemaService,
    $timeout: angular.ITimeoutService
  ) {
    super(jsonSchemaService, $timeout);
  }

  /**
   * Implement IController
   */
  public async $onInit() {
    super.$onInit();
    this.optionsTemplate = "salaxy-components/helpers/import/CalcImportOptions.html";
    this.options = {
      isMerge: true,
      rowMergeOption: RowMergeOption.RemoveAllRows,
      applyEmploymentDefaultRows: false,
      sourceId: null,
    };
  }

  /**
   * Updates options from the ui.
   */
  public updateOptions() {
    switch(this.customOptions) {
      case "addRows":
        this.options.rowMergeOption = undefined;
        break;
      case "removeImportedRows":
        this.options.rowMergeOption = RowMergeOption.RemoveImportedRows;
        break;
      case "removeAllRows":
        this.options.rowMergeOption = RowMergeOption.RemoveAllRows;
        break;
    }
  }

  /**
   * Options validation. If required, override this method in the inheritied class.
   */
  public hasInvalidOptions() {
    return this.optionsForm && this.optionsForm.$invalid;
  }

  /** Import data */
  public async import() {

    if (this.isImporting) {
      return;
    }

    await this.validate();
    if (!this.targetData) {
      return;
    }

    this.isImporting = true;
    this.isCanceled = false;

    for(const sourceRow of this.sourceData) {
      sourceRow.validation = null;
      sourceRow.importStatus = ApiImportStatus.Ignored;
    }
    this.table.setData(this.sourceData);
    await this.$timeout();

    this.importTotal = this.targetData.length;
    this.importCurrent = 0;

    // compose calculations
    // group by officialId, personalId and salaryDate
    const groups = Arrays.groupBy(this.targetData, this.getTargetKey);
    const imports = [];
    for(const key in groups) {
      const groupCalcs = groups[key];
      const firstCalc = groupCalcs.splice(0,1)[0];
      for(const calc of groupCalcs) {
        firstCalc.item.rows.push(... calc.item.rows);
      }
      imports.push(firstCalc);
    }

    const chunkSize = 10;
    const chunks: CalculationImport[][] = [];
    for (let i = 0; i < imports.length; i += chunkSize) {
        chunks.push(imports.slice(i, i + chunkSize));
    }

    let index = 0;
    for(const chunk of chunks) {
      try {
        if (this.isCanceled) {
          break;
        }
        chunk.splice(0, chunk.length, ... await this.importsApi.importCalculations(chunk, this.options ));
      } catch (error) {
        for( const importObj of chunk) {
          importObj.result = importObj.result ?? {};
          importObj.result.status = ApiImportStatus.Error,
          importObj.validation = {
            isValid: false,
            errors: [{
              key: null,
              type: ApiValidationErrorType.General,
              code: "Server error",
              msg: error?.data?.messageHtml || JSON.stringify(error?.data, Object.getOwnPropertyNames(error?.data)),
            }],
          };
        }
      }
      for(const result of chunk) {
        const sourceRows = this.sourceData.filter((x) => this.getSourceKey(x) == this.getTargetKey(result));
        for(const sourceRow of sourceRows) {
          sourceRow.validation = {
            isValid: result.validation?.isValid,
            errors: result.validation?.errors
          };
          sourceRow.importStatus = result.result?.status;
        }
        index += sourceRows.length;
        this.importCurrent = index;

        this.table.setData(this.sourceData);
        await this.$timeout();
      }
    }

    this.isImporting = false;
    await this.$timeout();
  }

  /** Help */
  public async help() {
    const helpData = [
        [Translations.get("SALAXY.NG1.CalcImport.help.title")],
        [""],
        [Translations.get("SALAXY.NG1.CalcImport.help.overview.title")],
        [Translations.get("SALAXY.NG1.CalcImport.help.overview.row1")],
        [Translations.get("SALAXY.NG1.CalcImport.help.overview.row2")],
        [""],
        ...this.getDefaultHelpDataForColumns(),
      ];

    await this.download(helpData);
  }

  /** Returns extra validator */
  public getExtraValidators(): Array<{ validate: () => void, buttonLabel: string }> {
    return [this.extraValidator];
  }

  protected async getImportMapper() {

    const rowMap = {
      rowType: "rowType",
      message: "message",
      kind: "data.kind",
      count: "count",
      price: "price",
      unit: "unit",
      // rowSourceId: "sourceId",
      vatPercent: "accounting.vatPercent",
    };

    const rowMapper = new DataMapper<CalculationImportRow, UserDefinedRow>("NG1/CalcImporController", "Palkkalaskelmat");
    await rowMapper.setSchemaFromCache(this.jsonSchemaService.getCache(), "salaxy-secure/schemas/v02.json", "UserDefinedRow", "target");
    rowMapper.setPaths(rowMap);
    rowMapper.generateSchema("CalculationImportRow", "source");

    const rowSelector = new DataMapperSelector<CalculationImportRow, CalculationImport>(null, "item.rows",
      (source) => {
        if (source.rowType && source.price > 0) {
          return rowMapper.doMap([source]);
        }
        return [];
      });

    const mapper = new DataMapper<CalculationImportRow, CalculationImport>("NG1/CalcImportController", "Palkkalaskelmat");
    const map = {
      companyOfficialId: "officialId",
      personalId: "item.worker.paymentData.socialSecurityNumber",
      employmentId: "item.worker.employmentId",
      occupationCode: "item.info.occupationCode",
      periodStart: "item.info.workStartDate",
      periodEnd: "item.info.workEndDate",
      workDescription: "item.info.workDescription",
      salarySlipMessage: "item.info.salarySlipMessage",
      salaryDate: "item.workflow.salaryDate",
      paymentChannel: "item.info.paymentChannel",
      payrollTitle: "parameters.payroll.input.title",
      // payrollSourceId: "parameters.payroll.input.sourceId",
      // sourceId: "item.info.sourceId",
      fixedTaxAmount: "item.worker.tax.fixedTaxAmount",
      rowType: rowSelector,
    };
    await mapper.setSchemaFromCache(this.jsonSchemaService.getCache(), "salaxy-secure/schemas/v02.json", "CalculationImport", "target");
    mapper.setPaths(map);
    mapper.generateSchema("CalculationImportRow", "source");

    mapper.source.schema.properties.occupationCode["format"] ="Occupation";
    for(const property in rowMapper.source.schema.properties) {
      mapper.source.schema.properties[property] = rowMapper.source.schema.properties[property];
    }

    return mapper;
  }

  /** Import data */
  private async validateWorkers() {

    if (this.isValidating) {
      return;
    }

    await this.validate();
    if (!this.targetData) {
      return;
    }

    this.isValidating = true;

    for(const sourceRow of this.sourceData) {
      sourceRow.validation = null;
      sourceRow.validation = {
        isValid: null, // null on purpose
        errors: [{
          type: ApiValidationErrorType.General,
          msg: Translations.get("SALAXY.UI_Terms.wait"),
        }],
      };
      sourceRow.importStatus = ApiImportStatus.Ignored;
    }
    this.table.setData(this.sourceData);
    await this.$timeout();

    const workerGroups = Arrays.groupBy(this.sourceData, this.getCheckSourceKey);
    const workerChecks: WorkerCheck[] = [];
    for(const workerGroupKey in workerGroups) {
      workerChecks.push( {
        officialId: workerGroups[workerGroupKey][0].companyOfficialId,
        id: workerGroups[workerGroupKey][0].employmentId || workerGroups[workerGroupKey][0].personalId,
      });
    }

    const chunkSize = 10;
    const chunks: WorkerCheck[][] = [];
    for (let i = 0; i < workerChecks.length; i += chunkSize) {
        chunks.push(workerChecks.slice(i, i + chunkSize));
    }

    for(const chunk of chunks) {
      try {
        chunk.splice(0, chunk.length, ... await this.importsApi.checkWorkers(chunk));
      } catch (error) {
        for( const checkObj of chunk) {
          checkObj.result = checkObj.result ?? {};
          checkObj.result.status = ApiCheckStatus.Error,
          checkObj.validation = {
            isValid: false,
            errors: [{
              key: null,
              type: ApiValidationErrorType.General,
              code: "Server error",
              msg: error?.data?.messageHtml || JSON.stringify(error?.data, Object.getOwnPropertyNames(error?.data)),
            }],
          };
        }
      }
      for(const result of chunk) {
        const sourceRows = this.sourceData.filter((x) => this.getCheckSourceKey(x) == this.getCheckTargetKey(result));
        for(const sourceRow of sourceRows) {
          sourceRow.validation = {
            isValid: result.validation.isValid && result.result.item.length == 1 ,
            errors: result.validation.isValid ? (
                    (result.result.item == null || result.result.item.length == 0) ? [ {msg: Translations.get("SALAXY.NG1.CalcImport.messageMissingEmployment"), type: ApiValidationErrorType.General,}] :
                    (result.result.item.length == 1) ? [ {msg: Translations.get("SALAXY.NG1.CalcImport.messageExistingEmployment"), type: ApiValidationErrorType.General,}] :
                    (result.result.item.length > 1) ? [ {msg: Translations.get("SALAXY.NG1.CalcImport.messageSeveralEmployments"), type: ApiValidationErrorType.General,}] : result.validation.errors) :
                    result.validation.errors
          };
        }

        this.table.setData(this.sourceData);
        await this.$timeout();
      }
    }
    this.isValidating = false;
    await this.$timeout();
  }

}